import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, App, Button, Form, Input, Modal, Select, Switch, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { createBinding, disableBinding, listBindings, type FederationBinding } from '@/api/federation';
import { listRoles } from '@/api/teamAccess';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import { EmptyState } from '@/components/EmptyState';
import { useAuth } from '@/auth/OlAuthContext';
import { formatAbsolute } from '@/lib/time';

const { Paragraph, Text } = Typography;

/**
 * The role targets a claim value may map to: the seven tenant-plane catalogue roles plus this
 * hub's live `x_` custom roles. `platform_admin` is DELIBERATELY not offered AND explicitly
 * refused if typed (G17) — the server refuses it too (UAM_ROLE_FORBIDDEN); the console explains
 * the fence instead of merely relaying a refusal toast.
 */
const TENANT_PLANE_ROLES = [
  'hub_admin', 'hub_operator', 'flow_designer', 'flow_approver', 'analyst', 'auditor', 'hub_user',
];

interface RoleMapRow {
  claimValue: string;
  role: string;
}

interface CreateForm {
  alias: string;
  protocol: 'OIDC' | 'SAML';
  issuer: string;
  authorizationUrl?: string;
  tokenUrl?: string;
  clientId?: string;
  clientSecret?: string;
  roleClaim?: string;
  roleMap?: RoleMapRow[];
  trustBrokerAcr?: boolean;
}

/**
 * B1 · UC2 §9 — identity federation (enterprise IdP brokering): their IdP, our realm. The G17
 * pins render as FIXED FACTS, never as choices: sync mode is FORCE (mappers re-apply every login,
 * so a removed enterprise group actually bites) and JIT provisioning lands on the `hub_user`
 * zero-grant floor (a federated user matching no mapper gets NOTHING, never a default analyst).
 * OIDC only in this slice — SAML is a named later arrival the server refuses with 501 by design.
 * `federation.*` is recovery-plane (G10): never delegable; the server re-checks every affordance
 * (§12), and a 403 here names the key and its holders instead of a bare error.
 */
export function FederationPanel() {
  const { message } = App.useApp();
  const { user } = useAuth();
  const canWrite = user?.roles?.includes('hub_admin') ?? false;
  const [bindings, setBindings] = useState<FederationBinding[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [customRoles, setCustomRoles] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<CreateForm>();
  const protocol = Form.useWatch('protocol', form);
  const roleMap = Form.useWatch('roleMap', form);

  const load = useCallback(async () => {
    setLoading(true);
    setUnavailable(false);
    setForbidden(false);
    try {
      setBindings(await listBindings());
    } catch (e) {
      const n = normalizeApiError(e);
      if (n.httpStatus === 403) setForbidden(true);
      else if (isNotAvailable(n)) setUnavailable(true);
      else message.error(n.message);
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    void load();
    // Live x_ roles widen the mapping targets; a failed read just narrows the offer (server re-checks).
    void listRoles('ACTIVE')
      .then((roles) => setCustomRoles(roles.map((r) => r.roleKey)))
      .catch(() => setCustomRoles([]));
  }, [load]);

  const roleOptions = useMemo(
    () => [
      { label: 'Catalogue (tenant plane)', options: TENANT_PLANE_ROLES.map((r) => ({ value: r })) },
      ...(customRoles.length
        ? [{ label: 'Custom roles (this hub)', options: customRoles.map((r) => ({ value: r })) }]
        : []),
      {
        // Shown disabled rather than hidden — the refusal EXPLAINS itself at the point of choice.
        label: 'Never mappable (G17)',
        options: [{
          value: 'platform_admin',
          disabled: true,
          label: 'platform_admin — the platform plane is not the tenant’s to grant',
        }],
      },
    ],
    [customRoles],
  );

  /** The G17 fence, client-side too: refuse before the wire, with the WHY. */
  const mapsPlatformAdmin = (roleMap ?? []).some((row) => row?.role === 'platform_admin');

  const submit = async (v: CreateForm) => {
    if (v.protocol === 'SAML') return; // the SAML notice replaces the form body; nothing to send
    if (mapsPlatformAdmin) return; // the alert below already explains; the server would refuse too
    const rows = (v.roleMap ?? []).filter((r) => r?.claimValue?.trim() && r?.role);
    const claimRoleMap = rows.length
      ? Object.fromEntries(rows.map((r) => [r.claimValue.trim(), r.role]))
      : undefined;
    setSubmitting(true);
    try {
      await createBinding({
        alias: v.alias.trim(),
        protocol: 'OIDC',
        issuer: v.issuer.trim(),
        authorizationUrl: v.authorizationUrl?.trim(),
        tokenUrl: v.tokenUrl?.trim(),
        clientId: v.clientId?.trim(),
        clientSecret: v.clientSecret || undefined,
        roleClaim: v.roleClaim?.trim() || undefined,
        claimRoleMap,
        trustBrokerAcr: v.trustBrokerAcr === true,
      });
      message.success(`Binding ${v.alias.trim()} created — the secret went to the identity realm, not to OneLattice`);
      setModalOpen(false);
      form.resetFields();
      void load();
    } catch (e) {
      message.error(normalizeApiError(e).message);
    } finally {
      setSubmitting(false);
    }
  };

  const disable = async (b: FederationBinding) => {
    try {
      await disableBinding(b.bindingId);
      message.success(`${b.alias} disabled — logins through it stop; link history stays`);
      void load();
    } catch (e) {
      message.error(normalizeApiError(e).message);
    }
  };

  const columns: ColumnsType<FederationBinding> = [
    { title: 'Alias', dataIndex: 'alias', render: (v: string) => <Text code>{v}</Text> },
    { title: 'Protocol', dataIndex: 'protocol', width: 90, render: (v: string) => <Tag>{v}</Tag> },
    {
      title: 'Issuer',
      dataIndex: 'issuer',
      render: (v: string) => (
        <Text style={{ fontSize: 12, maxWidth: 260, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {v}
        </Text>
      ),
    },
    {
      title: 'Sync / JIT floor',
      key: 'pins',
      width: 170,
      render: (_: unknown, b: FederationBinding) => (
        <span style={{ display: 'inline-flex', gap: 4 }}>
          <Tag color="geekblue" style={{ margin: 0 }}>{b.syncMode || 'FORCE'}</Tag>
          <Tag style={{ margin: 0 }}>{b.jitFloorRole || 'hub_user'}</Tag>
        </span>
      ),
    },
    {
      title: 'Role map',
      dataIndex: 'claimRoleMap',
      render: (m: Record<string, string> | null) =>
        m && Object.keys(m).length ? (
          <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
            {Object.entries(m).map(([claim, role]) => (
              <Tag key={claim} style={{ margin: 0 }}>{claim} → {role}</Tag>
            ))}
          </span>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>none — everyone lands as hub_user</Text>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 100,
      render: (s: string) => <Tag color={s === 'ACTIVE' ? 'green' : 'default'}>{s}</Tag>,
    },
    { title: 'Created', dataIndex: 'createdAt', width: 170, render: (v) => formatAbsolute(v) },
    ...(canWrite
      ? [
          {
            title: '',
            key: 'actions',
            width: 90,
            render: (_: unknown, b: FederationBinding) =>
              b.status === 'ACTIVE' ? (
                <Button size="small" danger onClick={() => void disable(b)}>
                  Disable
                </Button>
              ) : null,
          },
        ]
      : []),
  ];

  if (forbidden) {
    return (
      <Alert
        type="info"
        showIcon
        message="Your role can't read federation bindings"
        description={
          <span>
            Federation is gated by the <Text code>federation.read</Text> permission key, held by{' '}
            <Text code>hub_admin</Text>, <Text code>hub_operator</Text> and <Text code>auditor</Text>{' '}
            — see the Permission matrix tab. It is a recovery-plane key: who may enter through the
            door is never delegable. This screen hides nothing extra: the server refused the read.
          </span>
        }
      />
    );
  }

  if (unavailable) {
    return (
      <EmptyState
        title="Identity federation is not available"
        description="The IAM UC2 federation surface is not enabled in this deployment. When it is, enterprise customers sign in through their own IdP (Entra ID, Okta, Ping, ADFS) brokered into this hub's realm — their groups become roles and spokes through claim mappers, re-applied on every login."
      />
    );
  }

  return (
    <div>
      <Paragraph type="secondary" style={{ marginBottom: 12, maxWidth: 780, fontSize: 13 }}>
        Enterprise sign-in through the customer&apos;s own IdP, brokered into this hub&apos;s realm.
        Two facts are pinned and not configurable (G17): sync mode is <Text code>FORCE</Text> — claim
        mappers re-apply on every login, so a user removed from an enterprise group loses the mapped
        role at their next sign-in — and first-login provisioning lands on <Text code>hub_user</Text>,
        the zero-grant floor: a federated user matching no mapper can open nothing. The IdP client
        secret transits to the identity realm and is never stored or shown here.
      </Paragraph>
      {canWrite && (
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)} style={{ marginBottom: 12 }}>
          New binding
        </Button>
      )}
      {!loading && bindings.length === 0 ? (
        <EmptyState
          compact
          title="No IdP bindings yet"
          description="Local realm sign-in is the current (and fully supported) state. Bind an enterprise IdP when a customer brings their own — OIDC now; SAML is a named later arrival."
        />
      ) : (
        <Table rowKey="bindingId" size="small" columns={columns} dataSource={bindings}
               loading={loading} pagination={{ pageSize: 20, hideOnSinglePage: true }} />
      )}

      <Modal title="New IdP binding" open={modalOpen} onCancel={() => setModalOpen(false)}
             onOk={() => form.submit()} confirmLoading={submitting} okText="Create binding"
             width={640}
             okButtonProps={{ disabled: protocol === 'SAML' || mapsPlatformAdmin }}>
        <Form form={form} layout="vertical" initialValues={{ protocol: 'OIDC', trustBrokerAcr: false }}
              onFinish={(v) => void submit(v)}>
          <Form.Item name="protocol" label="Protocol" rules={[{ required: true }]}>
            <Select options={[{ value: 'OIDC' }, { value: 'SAML' }]} />
          </Form.Item>

          {protocol === 'SAML' ? (
            <Alert
              type="info"
              showIcon
              message="SAML is not yet supported (501 by design)"
              description="SAML brokering is a named later arrival — the server refuses it explicitly rather than half-supporting it. Bind this IdP over OIDC, or wait for the SAML slice."
            />
          ) : (
            <>
              <Form.Item name="alias" label="Alias" rules={[{ required: true, message: 'a short stable name' }]}
                         extra="The binding's stable name in the realm — one ACTIVE binding per alias.">
                <Input placeholder="acme-entra" />
              </Form.Item>
              <Form.Item name="issuer" label="Issuer" rules={[{ required: true }]}>
                <Input placeholder="https://login.example.com/tenant-id/v2.0" />
              </Form.Item>
              <Form.Item name="authorizationUrl" label="Authorization URL"
                         rules={[{ required: true, message: 'real IdPs do not derive this from the issuer' }]}>
                <Input placeholder="https://login.example.com/…/authorize" />
              </Form.Item>
              <Form.Item name="tokenUrl" label="Token URL"
                         rules={[{ required: true, message: 'real IdPs do not derive this from the issuer' }]}>
                <Input placeholder="https://login.example.com/…/token" />
              </Form.Item>
              <Form.Item name="clientId" label="Client id" rules={[{ required: true }]}>
                <Input placeholder="the app registration's client id" />
              </Form.Item>
              <Form.Item name="clientSecret" label="Client secret"
                         extra="Transits to the identity realm's provider config — OneLattice never stores, logs or echoes it.">
                <Input.Password placeholder="write-only" autoComplete="new-password" />
              </Form.Item>

              <Form.Item name="roleClaim" label="Role claim (optional)"
                         dependencies={[['roleMap']]}
                         extra="The IdP claim whose values map to roles below — required once the map has entries."
                         rules={[
                           ({ getFieldValue }) => ({
                             validator: (_, value: string | undefined) => {
                               const rows = (getFieldValue('roleMap') as RoleMapRow[] | undefined) ?? [];
                               if (rows.length > 0 && !value?.trim()) {
                                 return Promise.reject(new Error('a claim→role map needs the claim it reads — name it here'));
                               }
                               return Promise.resolve();
                             },
                           }),
                         ]}>
                <Input placeholder="groups" />
              </Form.Item>
              <Form.List name="roleMap">
                {(fields, { add, remove }) => (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                    {fields.map((field) => (
                      <div key={field.key} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <Form.Item name={[field.name, 'claimValue']} noStyle
                                   rules={[{ required: true, message: 'claim value' }]}>
                          <Input style={{ width: 220 }} placeholder="IdP group / claim value"
                                 aria-label="Claim value" />
                        </Form.Item>
                        <span aria-hidden>→</span>
                        <Form.Item name={[field.name, 'role']} noStyle
                                   rules={[{ required: true, message: 'role' }]}>
                          <Select style={{ width: 220 }} placeholder="role" options={roleOptions}
                                  showSearch aria-label="Mapped role" virtual={false} />
                        </Form.Item>
                        <Button size="small" type="text" icon={<DeleteOutlined />}
                                aria-label="Remove mapping" onClick={() => remove(field.name)} />
                      </div>
                    ))}
                    <Button size="small" icon={<PlusOutlined />} style={{ alignSelf: 'flex-start' }}
                            onClick={() => add({ claimValue: '', role: undefined })}>
                      Add claim→role mapping
                    </Button>
                  </div>
                )}
              </Form.List>
              {mapsPlatformAdmin && (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="platform_admin can never be a mapping target"
                  description="The platform plane is not the tenant's to grant: an IdP mapper reaching platform_admin would let an enterprise directory group mint platform operators (G17). The server refuses it too (UAM_ROLE_FORBIDDEN) — pick a tenant-plane or custom role."
                />
              )}

              <Form.Item name="trustBrokerAcr" label="Trust broker-asserted acr" valuePropName="checked"
                         extra="Off by default (§9.4). On, a step-up challenge accepts the IdP's own assurance assertion (e.g. WebAuthn done there) instead of re-challenging locally — a contractual trust decision, recorded in the federation evidence stream.">
                <Switch />
              </Form.Item>

              <Alert
                type="info"
                showIcon
                message="Pinned by design (not options)"
                description={
                  <span>
                    Sync mode <Text code>FORCE</Text>: mappers re-apply on every login — removal in the
                    enterprise directory bites at the next sign-in. JIT floor <Text code>hub_user</Text>:
                    an unmapped federated user is provisioned with zero grants. An empty role map is
                    valid: everyone lands as <Text code>hub_user</Text> until you map their groups.
                  </span>
                }
              />
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
}
